/**
 * Hotels along the route and around the destination.
 *
 * NO PRICES, deliberately. Every hotel API that carries live rates - Agoda,
 * Booking, Expedia - gates access behind a commercial partnership: an account
 * manager, a certification step, four to eight weeks, and an expectation of
 * booking volume this fleet does not have. Amadeus was the one self-serve free
 * tier and it was decommissioned on 17 July 2026. Rather than ship a price
 * that is stale, wrong, or a scraped figure that breaks whenever an OTA
 * changes its markup, the app shows WHERE the hotels are and hands off for
 * what a room actually costs tonight.
 *
 * The hand-off goes to a WEB SEARCH, not to Agoda. Agoda was tried first and
 * reported from the road: "it goes to agoda, but many times a generic agoda
 * page." Confirmed - their search URL drops the query and lands on the
 * homepage. A web search keeps the query, compares several booking sites in one
 * panel, and still finds the small Thai roadside places that no OTA lists,
 * together with their photos.
 *
 * That hand-off is a link, not an integration, so nothing here needs approval
 * and nothing breaks when someone else's terms change. If an affiliate
 * application is ever granted, prices drop into a list that already exists.
 *
 * The corridor logic is the same idea as fuelStops.ts, with one rule dropped:
 * a hotel on the far side of the road is a perfectly good hotel. You are
 * stopping for the night, so a U-turn costs a minute and does not matter -
 * whereas a fuel station on the wrong side costs a U-turn in traffic for
 * something you could get five kilometres later. Side is still REPORTED, since
 * "on your left" helps a tired driver spot it.
 */

import type { LngLat, Route } from '../core/types';
import { MAPBOX_TOKEN, REGION } from '../core/config';
import { romaniseThai } from '../core/translit';
import {
  bearing,
  bearingDelta,
  cumulativeDistances,
  distance,
  nearestPointOnSegment,
} from '../core/geo';

/**
 * How far off the route a hotel may sit, metres.
 *
 * The same 500 m the fuel search uses, at the driver's request. A wider radius
 * was tried at 3 km and it did not buy better hotels - it bought a longer list
 * of things a couple of towns off the road, which is not what "along the route"
 * means. Beside the road is the useful set.
 */
export const MAX_HOTEL_DETOUR_M = 500;

/**
 * Radius for "around here" and "at the destination", metres.
 *
 * Requested at 3-5 km: 500 m is right for a hotel BESIDE the route, where the
 * point is not to detour, but when the driver has stopped and is looking for a
 * bed, a few kilometres into town is exactly what they want.
 *
 * A cap is needed at all because Mapbox's category endpoint is proximity-RANKED
 * rather than proximity-limited - measured at Surin it returned results up to
 * 225 km away, which an unfiltered list would present as "nearby".
 */
export const NEARBY_RADIUS_M = 5_000;

/** Sample the route this far apart when looking for towns with hotels. */
const PROBE_SPACING_M = 40_000;

/** Never offer a hotel this close ahead - you cannot act on it in time. */
const MIN_AHEAD_M = 2_000;

/**
 * Most probe points per search.
 *
 * Each probe is four parallel category calls, so this is the real cost knob. Six
 * spreads acceptably over any Thai journey while keeping one search to a couple
 * of dozen requests on a car connection.
 */
const MAX_PROBES = 6;

export interface Hotel {
  id: string;
  /**
   * What to show: the English name where Mapbox has one, otherwise the Thai
   * name romanised. Measured over 120 hotels and resorts across six Thai
   * regions, 68% already carry an English name and only 1 of the remaining 38
   * had a usable `name_local` - so romanisation covers about a third of rows
   * and is worth having, but is not the common case.
   */
  name: string;
  /**
   * The name as Mapbox holds it, when that differs from `name`.
   *
   * Kept so the row can show the Thai underneath: a romanisation is for
   * READING, but the Thai is what is painted on the sign at the roadside and
   * what a local will recognise if the driver has to ask.
   */
  nameOriginal: string | null;
  /**
   * 'hotel' or 'resort' - a price HINT, not a price.
   *
   * There is no free API that gives real rates (see the file header), and a
   * made-up range would be read as real. The category is a fact we actually
   * have, and a resort genuinely sits in a different band from a roadside
   * hotel, so it is the honest version of the hint.
   */
  kind: 'hotel' | 'resort';
  /** Street address, already shortened for a single line. */
  address: string;
  center: LngLat;
  /** Phone from the POI record, when it has one. Drivers do ring ahead. */
  phone: string | null;
  /**
   * The hotel's own site, present for about 40% of them.
   *
   * Usually a Facebook page rather than a booking engine, which in Thailand is
   * where a small hotel actually posts its rates and photos - so it is often a
   * better answer to "what does it cost and what does it look like" than any
   * API we can reach.
   */
  website: string | null;
  /** Distance along the route to the turn-off, metres. Null for destination hits. */
  along: number | null;
  /** How far off the route it sits, metres. */
  offset: number;
  /** Which side of the road, for spotting it. Not used to filter - see above. */
  side: 'left' | 'right' | null;
  /** Straight-line distance from the destination, metres. Null for corridor hits. */
  fromDestination: number | null;
}

interface SearchBoxFeature {
  properties: {
    mapbox_id: string;
    name: string;
    name_preferred?: string;
    full_address?: string;
    place_formatted?: string;
    poi_category?: string[];
    metadata?: { phone?: string; website?: string };
    distance?: number;
  };
  geometry: { coordinates: LngLat };
}

/**
 * Categories searched.
 *
 * `lodging` was included at first and had to go: it is Mapbox's catch-all for
 * anywhere with a bed, so a BKK-Surin probe came back with หอพัก (student
 * dormitories), a ที่เรียน (language school) and a มหาวิทยาลัย hall. Those are
 * not places a driver can take a room for the night. hotel/motel/resort are
 * the three that mean what the driver means.
 */
const CATEGORIES = ['hotel', 'resort'];

/**
 * Names that are never a hotel however Mapbox has categorised them.
 *
 * หอพัก is a dormitory, ห้องพัก a let room, มหาวิทยาลัย a university. These
 * survive the category filter because their POI records carry a hotel tag, and
 * offering one to a driver at 22:00 wastes the stop.
 */
const NOT_A_HOTEL =
  /หอพัก|ห้องพัก|มหาวิทยาลัย|ที่เรียน|บ้านพัก|เกสต์เฮ|เกสท์เฮ|โฮมสเตย์|dormitory|dorm|hostel|guest ?house|homestay|farmstay|student|บ้านไร่/i;

async function searchCategory(
  category: string,
  point: LngLat,
  limit: number,
  signal?: AbortSignal,
): Promise<SearchBoxFeature[]> {
  const params = new URLSearchParams({
    access_token: MAPBOX_TOKEN,
    limit: String(limit),
    country: REGION.country,
    proximity: `${point[0]},${point[1]}`,
  });
  try {
    const res = await fetch(
      `https://api.mapbox.com/search/searchbox/v1/category/${category}?${params}`,
      { signal },
    );
    if (!res.ok) return [];
    const body = (await res.json()) as { features?: SearchBoxFeature[] };
    return body.features ?? [];
  } catch {
    // Offline or aborted - an empty list reads as "none found", which is the
    // right degradation for something entirely advisory.
    return [];
  }
}

/** Query several categories at one point and merge, keeping each POI once. */
async function searchLodgingNear(
  point: LngLat,
  limit: number,
  signal?: AbortSignal,
): Promise<SearchBoxFeature[]> {
  const lists = await Promise.all(
    CATEGORIES.map((category) => searchCategory(category, point, limit, signal)),
  );
  const seen = new Set<string>();
  const byName = new Set<string>();
  const out: SearchBoxFeature[] = [];
  for (const f of lists.flat()) {
    const id = f.properties.mapbox_id;
    if (!id || seen.has(id)) continue;

    const name = f.properties.name_preferred || f.properties.name || '';
    if (NOT_A_HOTEL.test(name)) continue;

    /*
     * Also dedupe on name+town. The same hotel is indexed once per category it
     * belongs to and gets a DIFFERENT mapbox_id in each, so an id-only check
     * let "Modern Inn" through twice in one list.
     */
    const key = `${name.toLowerCase()}|${f.properties.place_formatted ?? ''}`;
    if (byName.has(key)) continue;

    seen.add(id);
    byName.add(key);
    out.push(f);
  }
  return out;
}

/** Does this string contain Thai script? */
const hasThai = (s: string): boolean => /[฀-๿]/.test(s);

function toHotel(f: SearchBoxFeature): Omit<Hotel, 'along' | 'offset' | 'side' | 'fromDestination'> {
  const p = f.properties;
  const raw = p.name_preferred || p.name || 'Hotel';
  /*
   * Prefer a real English name over a romanisation every time - Mapbox has one
   * for about two thirds of hotels, and "The Olarn Residence" beats any
   * mechanical transliteration of the same place. Romanise only what is left.
   */
  /*
   * Thai business registrations often append the proprietor:
   * "โรงแรมวิวรีสอร์ท โดยนายเสี้ยง บุญจำนงค์" is "Wiew Resort Hotel, by Mr
   * Siang Bunchamnong". Romanised in full that becomes
   * "Hotel Wio Resort Doyanaysiang Buyachamnng" - a wall of syllables in which
   * the actual hotel name is the first third. Nothing after โดย ("by") helps a
   * driver find the place, so it is cut before romanising.
   */
  const trimmed = raw.split(/s*โดย(?:นาย|นาง|นางสาว)?s*/)[0].trim() || raw;
  const display = hasThai(trimmed) ? romaniseThai(trimmed) : trimmed;
  return {
    id: p.mapbox_id,
    name: display.slice(0, 48),
    // The full original, proprietor and all - it is what the signboard says.
    nameOriginal: display !== raw ? raw.slice(0, 48) : null,
    kind: (p.poi_category ?? []).includes('resort') ? 'resort' : 'hotel',
    // full_address repeats the country and postcode, which is noise on one
    // line; place_formatted is the town, which is what actually orients you.
    address: (p.place_formatted || p.full_address || '').slice(0, 60),
    center: f.geometry.coordinates,
    phone: p.metadata?.phone ?? null,
    website: p.metadata?.website ?? null,
  };
}

function projectOntoRoute(
  coords: LngLat[],
  cum: number[],
  p: LngLat,
): { along: number; offset: number; side: 'left' | 'right' } | null {
  let best: { d: number; i: number; t: number; point: LngLat } | null = null;
  for (let i = 0; i < coords.length - 1; i++) {
    const r = nearestPointOnSegment(p, coords[i], coords[i + 1]);
    if (!best || r.distance < best.d) best = { d: r.distance, i, t: r.t, point: r.point };
  }
  if (!best) return null;
  const a = coords[best.i];
  const b = coords[best.i + 1] ?? a;
  const side = bearingDelta(bearing(a, b), bearing(best.point, p)) < 0 ? 'left' : 'right';
  const along = (cum[best.i] ?? 0) + distance(a, b) * best.t;
  return { along, offset: best.d, side };
}

export interface HotelSearchOptions {
  route: Route;
  /** Where the vehicle is along the route now, metres. 0 when planning. */
  alongNow?: number;
  signal?: AbortSignal;
  /** Cap on results, before the caller trims further. */
  limit?: number;
}

/**
 * Hotels beside the route ahead.
 *
 * Probes every PROBE_SPACING_M along the remaining route rather than searching
 * the whole line: the category endpoint is proximity-ranked, so one call per
 * town along the way returns what is actually there, whereas a single call from
 * the start returns forty hotels in the first city and nothing beyond it.
 */
export async function findHotelsAhead(o: HotelSearchOptions): Promise<Hotel[]> {
  const coords = o.route.geometry.coordinates as LngLat[];
  if (coords.length < 2) return [];
  const cum = cumulativeDistances(coords);
  const total = cum[cum.length - 1] ?? 0;
  const from = Math.max(0, o.alongNow ?? 0);

  /*
   * Probe points, spread over the WHOLE remaining route rather than the first
   * few spacings of it. Fixed 40 km steps with a cap of six covered only the
   * first 240 km of a 426 km drive, so every hotel found sat within the first
   * hour and the destination end - the part a driver actually plans an
   * overnight around - was never searched. Spacing now derives from the
   * distance left, so the probes always reach the end.
   */
  const remaining = Math.max(0, total - from);
  const steps = Math.max(1, Math.min(MAX_PROBES, Math.round(remaining / PROBE_SPACING_M)));
  const probes: LngLat[] = [];
  for (let k = 1; k <= steps; k++) {
    const d = from + (remaining * k) / steps;
    let i = cum.findIndex((c) => c >= d);
    if (i < 0) i = coords.length - 1;
    probes.push(coords[i]);
  }

  const lists = await Promise.all(probes.map((p) => searchLodgingNear(p, 10, o.signal)));

  const seen = new Set<string>();
  const byName = new Set<string>();
  const out: Hotel[] = [];
  for (const f of lists.flat()) {
    if (!f.properties.mapbox_id || seen.has(f.properties.mapbox_id)) continue;
    // Adjacent probes overlap, so the same hotel arrives twice with the same id
    // AND sometimes twice with different ids - both checks are needed here, not
    // just inside searchLodgingNear which only sees one probe at a time.
    const nameKey = `${(f.properties.name_preferred || f.properties.name || '').toLowerCase()}|${
      f.properties.place_formatted ?? ''
    }`;
    if (byName.has(nameKey)) continue;
    const proj = projectOntoRoute(coords, cum, f.geometry.coordinates);
    if (!proj) continue;
    if (proj.offset > MAX_HOTEL_DETOUR_M) continue;
    // Must be ahead, with enough room to act on it.
    if (proj.along < from + MIN_AHEAD_M) continue;
    seen.add(f.properties.mapbox_id);
    byName.add(nameKey);
    out.push({
      ...toHotel(f),
      along: proj.along,
      offset: proj.offset,
      side: proj.side,
      fromDestination: null,
    });
  }

  out.sort((a, b) => (a.along ?? 0) - (b.along ?? 0));
  return out.slice(0, o.limit ?? 12);
}

/** Hotels around the destination, nearest first. */
export async function findHotelsNear(
  point: LngLat,
  limit = 8,
  signal?: AbortSignal,
  radiusM = NEARBY_RADIUS_M,
): Promise<Hotel[]> {
  // 25, not 10: with a radius filter below, a small page is mostly spent on
  // distant results that are then discarded, leaving too few nearby ones.
  const feats = await searchLodgingNear(point, 25, signal);
  const scored = feats
    .map((f) => ({
      ...toHotel(f),
      along: null,
      offset: 0,
      side: null,
      fromDestination: distance(point, f.geometry.coordinates),
    }))
    .sort((a, b) => (a.fromDestination ?? 0) - (b.fromDestination ?? 0));

  const near = scored.filter((h) => (h.fromDestination ?? Infinity) <= radiusM);

  /*
   * Fall back to the nearest few when the radius finds nothing.
   *
   * The category endpoint is proximity-RANKED, not proximity-limited: it
   * happily returns hotels 225 km away, so an unfiltered list could show
   * something in the next province as if it were nearby. A radius fixes that,
   * but out in rural Isan a 3 km circle can hold a single hotel - and "none
   * found" is the wrong answer when there is one 6 km up the road. So the cap
   * shapes the normal case, and the nearest three are shown when it is empty.
   */
  return (near.length ? near : scored.slice(0, 3)).slice(0, limit);
}

/**
 * Deep link to a booking site for prices and availability.
 *
 * A search URL rather than a property link: we hold no mapping from a Mapbox
 * POI to an Agoda or Booking property id, and guessing one would land the
 * driver on the wrong hotel. Searching the name and town gets them to the right
 * page in one tap and cannot be silently wrong.
 *
 * `affiliate` is threaded through for the day an affiliate application is
 * approved - both sites take the id as a query parameter, so the links start
 * earning without any other change. Absent, these are ordinary search links.
 */
export function bookingUrl(
  hotel: Hotel,
  site: 'web' | 'booking' | 'agoda' = 'web',
  affiliate?: string,
): string {
  /*
   * Search on the ORIGINAL name where there is one.
   *
   * A romanisation is for the driver to read; the booking sites index the name
   * the hotel registered under, which for a Thai hotel is the Thai one. Sending
   * "Rueanachumkhokamn" finds nothing, while เรือนชุมโคกมล finds the property.
   */
  const name = hotel.nameOriginal || hotel.name;
  const q = encodeURIComponent(`${name} ${hotel.address}`.trim());

  if (site === 'agoda') {
    /*
     * Agoda is NOT the default any more.
     *
     * Reported from the road: "when i click prices link in hotels, it goes to
     * agoda, but many times a generic agoda page." Confirmed - agoda.com/search
     * ?q=... redirects to the homepage, losing the query entirely, so the driver
     * arrives at a search box rather than the hotel. Their real search needs
     * property or city ids we do not hold. Kept only for an explicit choice.
     */
    const cid = affiliate ? `&cid=${encodeURIComponent(affiliate)}` : '';
    return `https://www.agoda.com/search?q=${q}${cid}`;
  }

  if (site === 'web') {
    /*
     * The DEFAULT, chosen over Booking deliberately.
     *
     * Google keeps the query across the redirect (Agoda does not), its hotel
     * panel compares Agoda/Booking/Trip prices in one place rather than one
     * site's, and - the deciding point for this fleet - it still finds the
     * small Thai roadside places that no OTA lists, along with their photos and
     * Facebook page. Booking is one bookable price but only for properties it
     * carries, and many hotels on these routes are not.
     */
    return `https://www.google.com/search?q=${q}`;
  }

  // Booking.com keeps the query across the redirect - verified - so it is the
  // one that reliably shows the property and its price.
  const aid = affiliate ? `&aid=${encodeURIComponent(affiliate)}` : '';
  return `https://www.booking.com/searchresults.html?ss=${q}${aid}`;
}
